// src/components/data/TableNavList.js
//
// Sidebar table navigation for the Data page. TableNavItem renders one table
// row plus its View Data / Manage Files / Settings actions submenu;
// TableNavList renders a group of tables (visible rows followed by a
// collapsible warning-tinted "Hidden (n)" bucket). Data.js renders one list
// per storage group - Decode tables and external sources - and keeps all
// selection/navigation state, passed in through callbacks.
//
// Visible rows can be dragged to reorder them within their group (native HTML5
// drag events, as IngestionRuleBuilder does). The list only reports
// (dragIndex, dropIndex) over its visible rows through `onReorder`; Data.js
// owns the persisted order (utils/tableOrder). Hidden rows are not draggable
// and a drop from another group's list is ignored — each list tracks its own
// in-flight drag in a ref, so a foreign drag never has an index here.
import React, { useRef, useState } from 'react';
import {
  Badge,
  Box,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TableChartIcon from '@mui/icons-material/TableChart';
import FolderIcon from '@mui/icons-material/Folder';
import SettingsIcon from '@mui/icons-material/Settings';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ErrorIcon from '@mui/icons-material/Error';
import { LANDING_ATTENTION_MESSAGE } from './manage/ingestionStatusDisplay';
import { isExternalTable } from '../../utils/tableTypes';
import {
  isTableBeingCreated, isTableCreationInterrupted, isTableDeletionInterrupted,
} from '../../utils/tableLifecycle';

// One row of the actions submenu under the selected table.
function TableActionItem({ pl, active, icon: Icon, label, onClick, badgeCount = 0, caption = null }) {
  return (
    <ListItemButton
      sx={{
        pl,
        py: 0.5,
        backgroundColor: (theme) => active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
      }}
      onClick={onClick}
    >
      <ListItemIcon sx={{ minWidth: 36 }}>
        <Badge badgeContent={badgeCount} color="warning" invisible={badgeCount === 0}>
          <Icon fontSize="small" sx={{ color: active ? 'primary.main' : 'text.secondary' }} />
        </Badge>
      </ListItemIcon>
      <ListItemText
        primary={label}
        sx={{ minWidth: 0, mr: 1 }}
        primaryTypographyProps={{
          variant: 'body2',
          noWrap: true,
          fontWeight: active ? 500 : 400
        }}
      />
      {caption && (
        <Typography variant="caption" color="warning.main" sx={{ mr: 1, whiteSpace: 'nowrap', flexShrink: 0 }}>
          {caption}
        </Typography>
      )}
    </ListItemButton>
  );
}

export function TableNavItem({
  table,
  selected,
  hidden = false,
  viewMode,
  actionsExpanded,
  filesNeedingAttention = 0,
  // The table's claim reads `blocked`: a landing needs support to finish.
  landingBlocked = false,
  onSelect,
  onToggleActions,
  onNavigate,
  // Present only on rows that can be reordered: the native drag event handlers
  // to spread onto the row. `dragOver` tints the row a drag is hovering.
  dragHandlers = null,
  dragOver = false,
}) {
  const external = isExternalTable(table);
  const basePl = hidden ? 4 : 2;
  const actionPl = hidden ? 6 : 4;
  const draggable = Boolean(dragHandlers);
  // create_table is still running: the name is shown so people can see it is taken, but
  // there is no table to open yet.
  const beingCreated = isTableBeingCreated(table);

  return (
    <React.Fragment>
      <ListItemButton
        selected={selected}
        disabled={beingCreated}
        onClick={beingCreated ? undefined : () => onSelect(table.id)}
        draggable={draggable}
        {...(dragHandlers || {})}
        sx={{
          pl: basePl,
          borderRadius: 1,
          mb: 0.5,
          position: 'relative',
          ...(hidden ? { opacity: 0.7 } : {}),
          ...(dragOver ? { backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08) } : {}),
          '&:hover .table-drag-handle': { opacity: 1 },
          '&.Mui-selected': {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
            ...(hidden ? { opacity: 1 } : {}),
            '&:hover': {
              backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.18),
            }
          }
        }}
      >
        {draggable && (
          // Sits inside the row's left padding so revealing it never shifts the name.
          <DragIndicatorIcon
            className="table-drag-handle"
            sx={{
              position: 'absolute',
              left: 0,
              fontSize: 16,
              color: 'text.disabled',
              opacity: 0,
              transition: 'opacity 0.15s ease',
              cursor: 'grab',
            }}
          />
        )}
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <Typography
                variant="body2"
                noWrap
                sx={{ fontWeight: selected ? 600 : 400 }}
              >
                {table.name}
              </Typography>
              {landingBlocked && (
                <Tooltip title={LANDING_ATTENTION_MESSAGE}>
                  <ErrorIcon color="error" sx={{ fontSize: 16 }} />
                </Tooltip>
              )}
              {external && (
                <Tooltip title="Read-only live source">
                  <Chip label="Live" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                </Tooltip>
              )}
              {isTableDeletionInterrupted(table) && (
                <Tooltip title="Deleting this table was interrupted; it can only be deleted again">
                  <Chip label="Deletion interrupted" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                </Tooltip>
              )}
              {beingCreated && (
                <Chip label="Creating…" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
              )}
              {isTableCreationInterrupted(table) && (
                <Tooltip title="Creating this table was interrupted; it can only be deleted">
                  <Chip label="Creation interrupted" size="small" color="warning" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                </Tooltip>
              )}
            </Box>
          }
        />
        {selected && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleActions(table.id);
            }}
            sx={{ p: 0.5 }}
          >
            {actionsExpanded ? <ExpandLessIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        )}
      </ListItemButton>

      {selected && (
        <Collapse in={actionsExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            <TableActionItem
              pl={actionPl}
              active={viewMode === 'data'}
              icon={TableChartIcon}
              label="View Data"
              onClick={() => onNavigate('data')}
            />
            {!external && (
              <TableActionItem
                pl={actionPl}
                active={viewMode === 'files'}
                icon={FolderIcon}
                label="Manage Files"
                onClick={() => onNavigate('files')}
                badgeCount={filesNeedingAttention}
                caption={filesNeedingAttention > 0
                  ? `${filesNeedingAttention} need${filesNeedingAttention === 1 ? 's' : ''} attention`
                  : null}
              />
            )}
            <TableActionItem
              pl={actionPl}
              active={viewMode === 'settings'}
              icon={SettingsIcon}
              label="Settings"
              onClick={() => onNavigate('settings')}
            />
          </List>
        </Collapse>
      )}
    </React.Fragment>
  );
}

function TableNavList({
  tables,
  hiddenMap,
  hiddenExpanded,
  onToggleHiddenExpanded,
  currentTableId,
  viewMode,
  expandedTableActions,
  filesNeedingAttention,
  // A Map from the id of each table whose claim reads `blocked` to its batch id.
  blockedLandingBatches,
  onSelectTable,
  onToggleActions,
  onNavigate,
  onReorder,
}) {
  const visibleTables = tables.filter(table => !hiddenMap[table.id]);
  const hiddenTables = tables.filter(table => hiddenMap[table.id]);

  // Index (into visibleTables) of the row being dragged from THIS list; null
  // while no drag is in flight here, which is how a foreign drop is rejected.
  const dragIndexRef = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const dragHandlersFor = (table, index) => ({
    onDragStart: (e) => {
      dragIndexRef.current = index;
      // Firefox needs some payload for a drag to start; the id is not read back.
      e.dataTransfer.setData('text/plain', table.id);
      e.dataTransfer.effectAllowed = 'move';
    },
    onDragOver: (e) => {
      if (dragIndexRef.current === null) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragOverIndex !== index) setDragOverIndex(index);
    },
    onDragLeave: () => setDragOverIndex(null),
    onDrop: (e) => {
      const dragIndex = dragIndexRef.current;
      if (dragIndex === null) return;
      e.preventDefault();
      dragIndexRef.current = null;
      setDragOverIndex(null);
      if (dragIndex !== index) onReorder(dragIndex, index);
    },
    onDragEnd: () => {
      dragIndexRef.current = null;
      setDragOverIndex(null);
    },
  });

  const itemProps = (table, hidden) => ({
    table,
    hidden,
    selected: currentTableId === table.id,
    viewMode,
    actionsExpanded: !!expandedTableActions[table.id],
    filesNeedingAttention,
    landingBlocked: blockedLandingBatches.has(table.id),
    onSelect: onSelectTable,
    onToggleActions,
    onNavigate,
  });

  return (
    <List component="div" disablePadding>
      {visibleTables.map((table, index) => (
        <TableNavItem
          key={table.id}
          {...itemProps(table, false)}
          dragHandlers={onReorder ? dragHandlersFor(table, index) : null}
          dragOver={dragOverIndex === index}
        />
      ))}

      {hiddenTables.length > 0 && (
        <>
          <ListItemButton
            onClick={onToggleHiddenExpanded}
            sx={{
              pl: 2,
              borderRadius: 1,
              mb: 0.5,
              mt: 1,
              backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.05),
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.1),
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <VisibilityOffIcon fontSize="small" sx={{ color: 'warning.main' }} />
            </ListItemIcon>
            <ListItemText
              primary={`Hidden (${hiddenTables.length})`}
              primaryTypographyProps={{
                variant: 'body2',
                color: 'warning.main',
                fontWeight: 500
              }}
            />
            <IconButton size="small" sx={{ p: 0.5 }}>
              {hiddenExpanded ? <ExpandLessIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          </ListItemButton>

          <Collapse in={hiddenExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {hiddenTables.map((table) => (
                <TableNavItem key={table.id} {...itemProps(table, true)} />
              ))}
            </List>
          </Collapse>
        </>
      )}
    </List>
  );
}

export default TableNavList;
